/**
 * VG 2.0 · Phase 11C — Owner Conversion Surface
 *
 * Public route: /proprietari
 *
 * The page performs ZERO backend calls and ZERO local persistence
 * writes on mount. It exists to bring an owner to `/vg2` — the
 * canonical owner entry that owns lifecycle logic
 * (CONTINUE_CURRENT / RESTART_SAME_PROPERTY / FULL_RESET).
 *
 * See docs/vg2/PHASE_11C_owner_conversion.md for the Claim Truth Matrix
 * and zero-mutation proof.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Check,
  ChevronRight,
  Compass,
  Home,
  Sparkles,
  FileText,
  Users,
  MessageCircle,
  Menu,
  X,
  History,
  Edit3,
  UserRound,
  Tag,
  KeyRound,
  Hammer,
  TrendingUp,
  ArrowUp,
} from "lucide-react";
import Logo from "@/components/landing/Logo";
import Footer from "@/components/landing/Footer";
import { HomePendingRequests } from "@/components/vg2/HomePendingRequests";
import OwnerHomeHero, { HOMEPAGE_SUGGESTIONS, useStartWithIntent } from "@/components/vg2/OwnerHomeHero";
import { useOwnerSignupPanel } from "@/components/vg2/OwnerSignupPanel";
import { MAGAZINE_META, getLatestArticles } from "@/content/magazine";
import { startJourney, getStoredJourneyId, getJourney } from "@/lib/vg2Journey";

// ---------- SEO (no react-helmet dependency) -----------------------------

function useLandingSEO() {
  React.useEffect(() => {
    const prevTitle = document.title;
    document.title = "La tua casa può fare di più | ValutaGenio";
    const metas = [
      {
        name: "description",
        content:
          "ValutaGenio parte dal tuo obiettivo, confronta le possibilità della casa e costruisce la prossima azione.",
      },
      { property: "og:title", content: "La tua casa può fare di più." },
      {
        property: "og:description",
        content: "Capisci cosa può darti, prima di decidere cosa farne.",
      },
      { property: "og:type", content: "website" },
    ];
    const attached = [];
    metas.forEach((m) => {
      const key = m.name ? "name" : "property";
      const val = m.name || m.property;
      let el = document.querySelector(`meta[${key}="${val}"]`);
      let created = false;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(key, val);
        document.head.appendChild(el);
        created = true;
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", m.content);
      attached.push({ el, prev, created });
    });
    let canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute("href") : null;
    let canonicalCreated = false;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
      canonicalCreated = true;
    }
    canonical.setAttribute("href", "/proprietari");
    return () => {
      document.title = prevTitle;
      attached.forEach(({ el, prev, created }) => {
        if (created) el.remove();
        else if (prev !== null) el.setAttribute("content", prev);
      });
      if (canonicalCreated) canonical.remove();
      else if (prevCanonical !== null) canonical.setAttribute("href", prevCanonical);
    };
  }, []);
}

// ---------- atoms --------------------------------------------------------

function Eyebrow({ children }) {
  return (
    <span className="inline-block text-[11px] uppercase tracking-[0.22em] font-semibold text-[#B40101] mb-4">
      {children}
    </span>
  );
}

function H2({ children, id }) {
  return (
    <h2
      id={id}
      className="font-serif text-[#0A0A0A] text-3xl md:text-4xl lg:text-[42px] leading-[1.15] tracking-tight max-w-3xl"
    >
      {children}
    </h2>
  );
}

function Lead({ children }) {
  return (
    <p className="mt-6 text-[#0A0A0A]/80 text-[17px] md:text-[18px] leading-relaxed max-w-2xl">
      {children}
    </p>
  );
}

// ---------- editorial photo slots ---------------------------------------
//
// Owner-landing editorial series (4 originali, 4:3, forniti dal cliente).
// Ogni slot resta vuoto finché `src` è null: l'atomo `Photo43` renderizza
// una placeholder tipografica con lo stesso rapporto 4:3 per proteggere
// il layout. Quando gli asset saranno approvati, valorizzare `src`
// (WebP servito da /photos/ o CDN) e lasciare invariato tutto il resto.
//
// Alt text descrittivo in italiano — pensato per screen reader e SEO
// senza rivelare token di dominio interni.

const OWNER_PHOTOS = {
  for_sale: {
    src: "/images/vg2/vg-situazione-immobile-da-vendere.webp",
    srcset: null,
    alt: "Proprietaria che installa il cartello 'Vendesi' davanti a un immobile, mentre valuta prezzo, tempi e strategia.",
    objectPosition: "center",
    label: "Immobile da vendere",
  },
  inherited: {
    src: "/images/vg2/vg-situazione-casa-ereditata.webp",
    srcset: null,
    alt: "Proprietario con le chiavi in una casa ereditata, davanti ai ricordi di famiglia.",
    objectPosition: "center",
    label: "Casa ereditata",
  },
  income: {
    src: "/images/vg2/vg-situazione-creare-reddito.webp",
    srcset: null,
    alt: "Proprietaria che valuta canone, sostenibilità e alternative per far produrre reddito alla casa.",
    objectPosition: "center",
    label: "Creare reddito",
  },
  transformation: {
    src: "/images/vg2/vg-situazione-casa-da-ristrutturare.webp",
    srcset: null,
    alt: "Proprietario in un appartamento da ristrutturare con planimetria, metro e campioni di materiali.",
    objectPosition: "center",
    label: "Casa da ristrutturare",
  },
  // NOTE: advisor bucket kept for backwards compatibility (used only
  // where a professional/owner meeting is illustrated). In VG V2 the
  // definitive Property Advisor photo is rendered exclusively inside
  // the AGISCI page — never on this landing.
  advisor: {
    src: null,
    srcset: null,
    alt: null,
    objectPosition: "center",
    label: "Property Advisor",
  },
};

function Photo43({ config, testid, priority = false, roundedClass = "rounded-xl", sizes }) {
  const { src, srcset, alt, objectPosition, label } = config;
  if (!src) {
    return (
      <div
        data-testid={`${testid}-slot`}
        role="img"
        aria-label={`Fotografia in arrivo: ${label}`}
        className={`w-full aspect-[4/3] bg-[#F0EEE9] border border-dashed border-[#D6D3CE] flex items-center justify-center ${roundedClass}`}
      >
        <span className="text-[11px] uppercase tracking-[0.22em] text-[#4B5563] px-4 text-center">
          Foto in arrivo · {label}
        </span>
      </div>
    );
  }
  return (
    <img
      src={src}
      srcSet={srcset}
      sizes={sizes || "(min-width: 768px) 50vw, 100vw"}
      alt={alt}
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      data-testid={testid}
      width={1448}
      height={1086}
      className={`w-full aspect-[4/3] object-cover ${roundedClass}`}
      style={{ objectPosition }}
    />
  );
}

// ---------- navbar -------------------------------------------------------

export function LandingNavbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [startingJourney, setStartingJourney] = useState(false);
  const menuRef = useRef(null);
  const btnRef = useRef(null);
  const navigate = useNavigate();

  // Menu "Inizia": create (or resume) a Capisci journey and navigate
  // straight to /vg2/capisci/:journey_id, skipping the intermediate
  // /vg2 landing page. Same logic as CapisciEntry.onStart + resume check.
  const handleStartJourney = async (e) => {
    e.preventDefault();
    if (startingJourney) return;
    setStartingJourney(true);
    setMenuOpen(false);
    try {
      const stored = getStoredJourneyId();
      if (stored) {
        try {
          const js = await getJourney(stored);
          if (!js.completed) {
            navigate(`/vg2/${stored}`);
            return;
          }
        } catch { /* stale id — fall through to new journey */ }
      }
      const js = await startJourney();
      navigate(`/vg2/${js.journey_id}`);
    } catch {
      // fallback to the intermediate page which shows a friendly error state
      navigate("/vg2");
    } finally {
      setStartingJourney(false);
    }
  };

  // Close on outside click, Escape, or route change.
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      setMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <header
      data-testid="owner-landing-navbar"
      className="sticky top-0 z-50 w-full bg-white/95 backdrop-blur-sm border-b border-[#E5E7EB]"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 min-h-16 py-2 flex items-center justify-between gap-3">
        <div data-testid="owner-landing-logo-home" className="flex flex-col justify-center leading-tight">
          <Logo size="text-xl md:text-2xl" />
          <span
            data-testid="owner-landing-logo-claim"
            className="block mt-1 text-[12px] sm:text-[13px] md:text-[15px] font-bold text-[#0A0A0A] leading-tight max-w-[260px] sm:max-w-none"
          >
            La piattaforma delle operazioni immobiliari
          </span>
        </div>

        <nav className="flex items-center gap-3 md:gap-6 text-sm">
          <Link
            to="/percorso"
            data-testid="owner-landing-nav-como"
            className="text-[#0A0A0A]/80 hover:text-[#B40101] transition-colors uppercase tracking-wide text-xs md:text-sm font-medium"
          >
            Come funziona
          </Link>

          <div className="relative">
            <button
              ref={btnRef}
              type="button"
              data-testid="owner-landing-nav-menu-button"
              aria-label={menuOpen ? "Chiudi menu" : "Apri menu"}
              aria-expanded={menuOpen}
              aria-haspopup="true"
              onClick={() => setMenuOpen((v) => !v)}
              className="inline-flex items-center justify-center w-10 h-10 rounded-lg text-[#0A0A0A] hover:bg-[#F5F5F5] transition-colors"
            >
              {menuOpen
                ? <X className="w-5 h-5" strokeWidth={2.2} />
                : <Menu className="w-5 h-5" strokeWidth={2.2} />}
            </button>

            {menuOpen && (
              <div
                ref={menuRef}
                role="menu"
                data-testid="owner-landing-nav-menu-panel"
                className="absolute right-0 mt-2 w-56 rounded-xl border border-[#E5E7EB] bg-white shadow-lg overflow-hidden"
              >
                <a
                  href="/vg2"
                  role="menuitem"
                  data-testid="owner-landing-nav-menu-inizia"
                  onClick={handleStartJourney}
                  aria-busy={startingJourney}
                  className="block px-4 py-3 text-[#0A0A0A] hover:bg-[#F5F5F5] hover:text-[#B40101] transition-colors text-sm font-medium cursor-pointer"
                >
                  {startingJourney ? "Sto preparando…" : "Inizia"}
                </a>
                <Link
                  to="/valore-immobiliare"
                  role="menuitem"
                  data-testid="owner-landing-nav-menu-magazine"
                  onClick={closeMenu}
                  className="block px-4 py-3 text-[#0A0A0A] hover:bg-[#F5F5F5] hover:text-[#B40101] transition-colors text-sm font-medium border-t border-[#F0F0F0]"
                >
                  Valore Immobiliare
                </Link>
                <Link
                  to="/property-advisor"
                  role="menuitem"
                  data-testid="owner-landing-nav-menu-professionisti"
                  onClick={closeMenu}
                  className="block px-4 py-3 text-[#0A0A0A] hover:bg-[#F5F5F5] hover:text-[#B40101] transition-colors text-sm font-medium border-t border-[#F0F0F0]"
                >
                  Professionisti
                </Link>
                <Link
                  to="/login"
                  role="menuitem"
                  data-testid="owner-landing-nav-menu-accedi"
                  onClick={closeMenu}
                  className="block px-4 py-3 text-[#0A0A0A] hover:bg-[#F5F5F5] hover:text-[#B40101] transition-colors text-sm font-medium border-t border-[#F0F0F0]"
                >
                  Accedi
                </Link>
              </div>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
}

// ---------- 1. HERO ------------------------------------------------------

function Hero() {
  const startWithIntent = useStartWithIntent();
  const heroCases = [
    { icon: Tag, label: "Voglio vendere", code: "vendere", intentLabel: "Vendere l'immobile" },
    { icon: KeyRound, label: "Voglio affittare", code: "generare_reddito", intentLabel: "Affittare l'immobile" },
    { icon: Hammer, label: "Voglio ristrutturare", code: "capire_quale_scelta_conviene", intentLabel: "Ristrutturare o valorizzare l'immobile" },
    { icon: TrendingUp, label: "Voglio mettere a reddito", code: "generare_reddito", intentLabel: "Mettere a reddito l'immobile" },
  ];
  return (
    <section
      id="inizia"
      data-testid="owner-landing-hero"
      className="relative border-b border-[#E5E7EB] overflow-hidden scroll-mt-16"
    >
      <div className="absolute inset-0 -z-0">
        <img
          src="/images/vg2/vg-home-hero-immobile-obiettivi.webp"
          alt=""
          aria-hidden="true"
          width={1672} height={941}
          loading="eager" decoding="async"
          data-testid="owner-landing-hero-photo"
          className="w-full h-full object-cover object-[62%_center] md:object-right"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#F9F9F9] via-[#F9F9F9]/95 to-[#F9F9F9]/0 md:from-[#F9F9F9] md:via-[#F9F9F9]/85 md:to-[#F9F9F9]/0" />
      </div>
      <div className="relative max-w-[1200px] mx-auto px-5 md:px-8 pt-14 md:pt-24 pb-14 md:pb-24">
        <div className="max-w-[720px]">
          <h1
            data-testid="owner-landing-hero-h1"
            className="font-display font-extrabold text-[#0A0A0A] text-4xl sm:text-5xl md:text-6xl lg:text-[64px] leading-[1.05] tracking-tight"
          >
            Un immobile. Molte possibilità.
          </h1>
          <p
            data-testid="owner-landing-hero-steps"
            className="mt-4 md:mt-5 text-sm md:text-base font-semibold uppercase tracking-[0.24em] text-[#B40101] no-underline"
          >
            VALUTA · DECIDI · AGISCI
          </p>
          <p
            data-testid="owner-landing-hero-lede"
            className="mt-5 md:mt-6 text-[#0A0A0A] text-lg md:text-xl leading-relaxed font-light max-w-xl"
          >
            Valuta, confronta le alternative e scopri come trasformare il
            potenziale immobiliare in un&apos;operazione concreta.
          </p>
          <div className="mt-8 max-w-[720px]">
            <HomePendingRequests />
            <OwnerHomeHero />
          </div>

          <p
            data-testid="home-hero-raccordo"
            className="mt-7 md:mt-8 max-w-[720px] text-xs md:text-sm font-semibold uppercase tracking-[0.18em] text-[#0A0A0A]/60"
          >
            Oppure clicca su una delle scelte seguenti
          </p>

          <div className="mt-3 max-w-[720px]" data-testid="home-hero-possibilita">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              {heroCases.map(({ icon: Icon, label, code, intentLabel }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => startWithIntent(code, intentLabel)}
                  data-testid={`home-hero-caso-${label.split(' ').pop().toLowerCase()}`}
                  className="group text-left rounded-xl border border-[#E5E7EB] bg-white/95 backdrop-blur-sm p-4 flex flex-col gap-3 hover:border-[#B40101] hover:-translate-y-0.5 transition-all duration-200 shadow-[0_8px_24px_-18px_rgba(0,0,0,0.3)]"
                >
                  <span className="w-10 h-10 rounded-full bg-[#B40101]/10 inline-flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#B40101]" strokeWidth={1.8} />
                  </span>
                  <span className="font-semibold text-[#0A0A0A] text-[15px] leading-snug inline-flex items-center gap-1">
                    {label}
                    <ArrowRight className="w-3.5 h-3.5 text-[#B40101] opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Finestra bianca centrata sotto l'Hero: raccordo alle quattro scelte soprastanti.
function PossibilitaWindow() {
  return (
    <section
      data-testid="home-possibilita-window"
      className="bg-white border-b border-[#E5E7EB]"
    >
      <div className="max-w-[860px] mx-auto px-5 md:px-8 py-14 md:py-20">
        <div className="rounded-3xl bg-[#F7F7F7] px-6 md:px-14 py-12 md:py-16 text-center">
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#B40101]/10 mb-6" aria-hidden="true">
            <ArrowUp className="w-6 h-6 text-[#B40101]" />
          </span>
          <p className="text-xs md:text-sm font-semibold uppercase tracking-[0.2em] text-[#B40101]">
            Le possibilità
          </p>
          <h2 className="mt-2 font-display font-bold text-[#0A0A0A] text-3xl md:text-4xl leading-tight">
            Da dove vuoi partire?
          </h2>
          <p className="mt-5 text-[16px] md:text-[18px] text-[#0A0A0A]/75 leading-relaxed">
            Ogni immobile può diventare il punto di partenza per un progetto diverso. Che tu voglia vendere, affittare, ristrutturare o investire, scegli l&apos;obiettivo che desideri esplorare. ValutaGenio ti aiuterà a comprendere le possibilità e a individuare la strada più adatta.
          </p>
        </div>
      </div>
    </section>
  );
}


function LegacyHeroDead() {
  return (
    <section
      data-testid="owner-landing-hero-legacy"
      className="hidden"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-16 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-7">
          <Eyebrow>ValutaGenio per i proprietari</Eyebrow>
          <h1
            className="font-serif text-[#0A0A0A] text-4xl sm:text-5xl lg:text-[60px] leading-[1.05] tracking-tight"
          >
            La tua casa può fare di più.
          </h1>
          <p
            className="mt-6 text-[#0A0A0A] text-[19px] md:text-[22px] leading-snug max-w-xl font-medium"
          >
            Capisci cosa può darti, prima di decidere cosa farne.
          </p>
          <p
            className="mt-5 text-[#0A0A0A]/80 text-[16px] md:text-[17px] leading-relaxed max-w-xl"
          >
            Una casa ereditata, una vendita ferma, un immobile che costa e non produce. ValutaGenio parte dalla situazione che hai oggi e costruisce la prossima azione.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
            <Link
              to="/vg2"
              className="inline-flex items-center gap-2 bg-[#B40101] hover:bg-[#8F0101] text-white font-semibold text-base px-6 py-3.5 rounded-xl transition-all duration-200 hover:-translate-y-0.5 shadow-[0_10px_30px_rgba(180,1,1,0.28)]"
            >
              Rispondi alla prima domanda <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href="#come-funziona"
              className="inline-flex items-center gap-2 text-[#0A0A0A] hover:text-[#B40101] font-medium text-base px-2 py-2 transition-colors"
            >
              Guarda come funziona <ChevronRight className="w-5 h-5" />
            </a>
          </div>
        </div>

        <aside className="lg:col-span-5">
          <img
            src="/images/vg2/vg-home-hero-immobile-obiettivi.webp"
            alt="Proprietaria in casa, davanti alla finestra, con planimetria, taccuino e chiavi sul tavolo."
            width={1672}
            height={941}
            loading="eager"
            decoding="async"
            className="w-full aspect-[16/9] object-cover rounded-2xl shadow-[0_20px_50px_-25px_rgba(0,0,0,0.15)]"
          />
        </aside>
      </div>
    </section>
  );
}

// ---------- 2. Situazioni che riconosci ----------------------------------

function SituationsSection() {
  const startWithIntent = useStartWithIntent();
  const items = [
    {
      photoKey: "income",
      testid: "owner-landing-photo-income",
      title: "Creare reddito",
      body: "Affitto classico, breve termine, universitario o turistico: misura sostenibilità e canone possibile per ogni strada.",
      cta: "Esplora questa possibilità →",
      intentCode: "generare_reddito",
      intentLabel: "Creare una rendita",
    },
    {
      photoKey: "for_sale",
      testid: "owner-landing-photo-for-sale",
      title: "Immobile da vendere",
      body: "Scopri il valore reale e la strategia migliore per la vendita.",
      cta: "Inizia la valutazione →",
      intentCode: "vendere",
      intentLabel: "Vendere l'immobile",
    },
    {
      photoKey: "transformation",
      testid: "owner-landing-photo-transformation",
      title: "Casa da ristrutturare",
      body: "Capisci quali interventi possono aumentare utilità e valore.",
      cta: "Valuta questa strada →",
      intentCode: "capire_quale_scelta_conviene",
      intentLabel: "Ristrutturare o valorizzare l'immobile",
    },
    {
      photoKey: "inherited",
      testid: "owner-landing-photo-inherited",
      title: "Casa ereditata",
      body: "Metti ordine tra valore, costi, documenti e possibilità.",
      cta: "Parti da qui →",
      intentCode: "capire_quale_scelta_conviene",
      intentLabel: "Decidere cosa fare di una casa ereditata",
    },
  ];
  return (
    <section className="bg-white border-b border-[#E5E7EB]">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24">
        <Eyebrow>Situazioni che riconosci</Eyebrow>
        <H2>Da quale situazione parti?</H2>
        <Lead>
          Ogni casa pone domande diverse. Scegli la situazione più vicina alla tua oppure descrivila con parole tue.
        </Lead>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {items.map(({ title, body, cta, photoKey, testid, intentCode, intentLabel }) => (
            <button
              key={title}
              type="button"
              onClick={() => startWithIntent(intentCode, intentLabel)}
              data-testid={`situazione-${photoKey}`}
              className="group text-left rounded-2xl border border-[#E5E7EB] bg-white overflow-hidden flex flex-col hover:border-[#B40101] transition-colors"
            >
              {photoKey ? (
                <Photo43
                  config={OWNER_PHOTOS[photoKey]}
                  testid={testid}
                  roundedClass=""
                  sizes="(min-width: 1200px) 580px, (min-width: 768px) 45vw, 100vw"
                />
              ) : null}
              <div className="p-6 md:p-7">
                <h3 className="font-semibold text-[#0A0A0A] text-lg mb-2">{title}</h3>
                <p className="text-[15px] text-[#0A0A0A]/80 leading-relaxed">{body}</p>
                <p className="mt-4 text-sm font-semibold text-[#B40101] inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                  {cta}
                </p>
              </div>
            </button>
          ))}
        </div>
        <div className="mt-12 md:mt-14 text-center">
          <p className="text-base md:text-lg text-[#0A0A0A] leading-relaxed max-w-2xl mx-auto"
             data-testid="owner-landing-situations-freetext-hint">
            Non trovi la tua situazione? Descrivi il tuo caso e ti aiutiamo a orientarti.
          </p>
          <button
            type="button"
            data-testid="owner-landing-situations-freetext-cta"
            onClick={() => {
              const el = document.querySelector('[data-testid=owner-home-hero-input]');
              if (el) { el.scrollIntoView({behavior:"smooth", block:"center"}); el.focus(); }
            }}
            className="mt-6 inline-flex items-center gap-3 bg-[#B40101] hover:bg-[#8F0101] text-white font-semibold text-base md:text-lg px-6 md:px-8 py-3.5 md:py-4 rounded-xl transition-colors shadow-sm"
          >
            <span>Raccontaci la tua situazione</span>
            <ArrowRight className="w-5 h-5" strokeWidth={2.2} />
          </button>
        </div>
      </div>
    </section>
  );
}

// ---------- 3. Parti dall'obiettivo --------------------------------------

function StartFromObjectiveSection() {
  return (
    <section className="bg-[#F9F9F9] border-b border-[#E5E7EB]">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-6">
          <Eyebrow>Parti da quello che vuoi ottenere</Eyebrow>
          <H2>La prima domanda non riguarda l&apos;immobile. Riguarda te.</H2>
          <Lead>
            La stessa casa può servire obiettivi diversi: liberare liquidità, dare una casa a un figlio, avere una rendita, ridurre le spese, valorizzare un immobile prima della vendita. ValutaGenio parte da qui.
          </Lead>
          <p className="mt-4 text-[15px] text-[#0A0A0A]/75 leading-relaxed max-w-xl">
            Solo dopo aver capito cosa vuoi ottenere ha senso guardare al mercato, ai vincoli e alle possibilità dell&apos;immobile.
          </p>
        </div>
        <div className="lg:col-span-6">
          <ul className="rounded-2xl bg-white border border-[#E5E7EB] p-6 md:p-8 space-y-3">
            {[
              "Vendere entro un tempo definito",
              "Ottenere una rendita stabile con la locazione",
              "Valorizzare l'immobile prima della vendita",
              "Ridurre le spese di gestione",
              "Sistemare aspetti tecnici o documentali rimasti aperti",
              "Capire se è il momento giusto per agire",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 text-[15px] text-[#0A0A0A]">
                <Check className="w-5 h-5 text-[#B40101] mt-0.5 shrink-0" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// ---------- 4. Capisci · Valuta · Decidi · Agisci ------------------------

function FourStepsSection() {
  const steps = [
    {
      k: "Capisci",
      title: "Il tuo obiettivo e il contesto dell'immobile",
      body: "Poche domande mirate per mettere in fila cosa vuoi ottenere, dove si trova la casa, com'è composta e quali vincoli o urgenze esistono.",
      limit: "Non serve una perizia. Bastano le informazioni che già conosci.",
    },
    {
      k: "Valuta",
      title: "Un quadro economico basato sulle evidenze disponibili",
      body: "Comprendi caratteristiche, valore e potenziale dell'immobile.",
      limit: "Non è una perizia legale, non è un valore garantito, non è un prezzo di vendita.",
    },
    {
      k: "Decidi",
      title: "La direzione che ha senso per te",
      body: "Esplora e confronta le operazioni possibili, considerando benefici, costi e vincoli quando disponibili.",
      limit: "La decisione resta esplicita e tua. ValutaGenio la registra.",
    },
    {
      k: "Agisci",
      title: "Un piano concreto e, se serve, i professionisti",
      body: "Individua i passi necessari per realizzare l'operazione scelta.",
      limit: "I professionisti diventano visibili solo dopo la selezione esplicita per il tuo caso.",
    },
  ];
  return (
    <section
      id="come-funziona"
      data-testid="owner-landing-como-funziona"
      className="bg-white border-b border-[#E5E7EB]"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24">
        <Eyebrow>Come funziona</Eyebrow>
        <H2>Capisci. Valuta. Decidi. Agisci.</H2>
        <Lead>
          Quattro passaggi, un solo caso. Ogni passaggio prepara il successivo, e i limiti sono chiari dove servono.
        </Lead>
        <div className="mt-12 space-y-4">
          {steps.map((s, i) => (
            <article
              key={s.k}
              className="rounded-2xl border border-[#E5E7EB] bg-white p-6 md:p-8 grid grid-cols-1 md:grid-cols-12 gap-6 items-start"
            >
              <div className="md:col-span-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full border-2 border-[#B40101] text-[#B40101] font-semibold inline-flex items-center justify-center text-sm">
                  {i + 1}
                </div>
                <div className="text-[11px] uppercase tracking-widest text-[#B40101] font-semibold">
                  {s.k}
                </div>
              </div>
              <div className="md:col-span-9">
                <h3 className="font-semibold text-[#0A0A0A] text-lg mb-2">{s.title}</h3>
                <p className="text-[15px] text-[#0A0A0A]/80 leading-relaxed">{s.body}</p>
                <p className="mt-3 text-[13px] text-[#4B5563] italic">{s.limit}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- 5. Cosa può restituirti ValutaGenio --------------------------

function OutputsSection() {
  const items = [
    ["Un orientamento legato al tuo obiettivo", "Sempre disponibile a partire da CAPISCI."],
    ["Un intervallo economico con riferimento centrale", "Solo quando ci sono anchor compatibili nell'evidenza."],
    ["Un orientamento e una prossima azione", "Quando un valore numerico affidabile non è disponibile."],
    ["Direzioni alternative confrontabili", "Vendere, affittare, valorizzare, mantenere — con le condizioni di ciascuna."],
    ["Prerequisiti e documenti da chiarire", "Emergono durante il percorso, in modo strutturato."],
    ["Una decisione esplicita del proprietario", "Registrata come tua, non generata dal sistema."],
    ["Un piano d'azione con prossima azione", "Ruoli professionali identificati per nome canonico."],
    ["Un documento riepilogativo scaricabile", "Quando il caso ha almeno decisione e piano."],
  ];
  return (
    <section className="bg-[#F9F9F9] border-b border-[#E5E7EB]">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24">
        <Eyebrow>Cosa può restituirti</Eyebrow>
        <H2>Non un valore secco. Un percorso che tiene traccia di ciò che sai e di ciò che manca.</H2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map(([title, cond]) => (
            <div
              key={title}
              className="rounded-xl border border-[#E5E7EB] bg-white p-5 md:p-6"
            >
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#B40101] mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium text-[#0A0A0A] text-[15px]">{title}</div>
                  <div className="text-[13px] text-[#4B5563] mt-1">{cond}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- 6. Quando entrano EVA, il PA e i professionisti --------------

function EvaAndProSection() {
  return (
    <section className="bg-white border-b border-[#E5E7EB]">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24">
        <Eyebrow>Persone, non solo strumenti</Eyebrow>
        <H2>Quando entrano EVA, il Property Advisor e i professionisti.</H2>

        {/* Feature block · Property Advisor (foto definitiva vive solo
            in AGISCI, qui restiamo full-text). */}
        <article
          data-testid="owner-landing-pa-feature"
          className="mt-12 rounded-2xl border border-[#B40101]/30 bg-[#F9F9F9] overflow-hidden"
        >
          <div className="p-6 md:p-8 lg:p-10">
            <div className="w-10 h-10 rounded-lg bg-[#B40101]/10 text-[#B40101] inline-flex items-center justify-center mb-4">
              <Users className="w-5 h-5" />
            </div>
            <h3 className="font-semibold text-[#0A0A0A] text-xl md:text-2xl mb-3">Property Advisor</h3>
            <p className="text-[15px] md:text-[16px] text-[#0A0A0A]/80 leading-relaxed max-w-3xl">
              <strong>ValutaGenio mette il proprietario al centro delle
              decisioni immobiliari. E non lo lascia solo.</strong> Il
              Property Advisor ti aiuta a leggere il quadro, verificare
              i passaggi decisivi e portare la direzione scelta dentro
              la realtà.
            </p>
            <p className="text-[14px] text-[#0A0A0A]/70 leading-relaxed mt-3 max-w-3xl">
              Il confronto parte soltanto da una tua richiesta. Non
              comporta incarichi o impegni commerciali. Eventuali servizi
              successivi saranno valutati e accettati da te, in modo
              esplicito.
            </p>
          </div>
        </article>

        {/* EVA + Professionisti · card testuali senza foto */}
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="rounded-2xl border border-[#E5E7EB] bg-white p-6 md:p-7">
            <div className="w-10 h-10 rounded-lg bg-[#B40101]/10 text-[#B40101] inline-flex items-center justify-center mb-4">
              <MessageCircle className="w-5 h-5" />
            </div>
            <h3 className="font-semibold text-[#0A0A0A] text-lg mb-2">EVA</h3>
            <p className="text-[15px] text-[#0A0A0A]/80 leading-relaxed">
              Chiarisce dubbi durante il percorso: cosa significa un'evidenza, come si legge una decisione, cosa cambia se una condizione manca. Non conduce la consulenza professionale.
            </p>
          </div>
          <div className="rounded-2xl border border-[#E5E7EB] bg-white p-6 md:p-7">
            <div className="w-10 h-10 rounded-lg bg-[#B40101]/10 text-[#B40101] inline-flex items-center justify-center mb-4">
              <Users className="w-5 h-5" />
            </div>
            <h3 className="font-semibold text-[#0A0A0A] text-lg mb-2">I professionisti coinvolti</h3>
            <p className="text-[15px] text-[#0A0A0A]/80 leading-relaxed">
              Architetti, geometri, notai, commercialisti e altre figure entrano nel caso solo dopo una selezione esplicita del Property Advisor. Non c&apos;è una lista pubblica da sfogliare, non ci sono ranking né recensioni.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- 7. Vista reale del prodotto ----------------------------------

function ProductViewSection() {
  return (
    <section className="bg-[#F9F9F9] border-b border-[#E5E7EB]">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24">
        <Eyebrow>Il tuo caso, in un'unica pagina</Eyebrow>
        <H2>Quello che vedi al termine di VALUTA → DECIDI.</H2>
        <Lead>
          Nessuna dashboard piena di numeri decorativi. Solo lo stato
          effettivo del tuo caso: la direzione scelta con la sua
          analisi economica, la lista dei documenti richiesti con lo
          stato reale di ognuno, e la prossima cosa da fare.
        </Lead>
        <div className="mt-12 mx-auto max-w-3xl rounded-2xl border border-[#E5E7EB] bg-white shadow-[0_20px_50px_-25px_rgba(0,0,0,0.15)] overflow-hidden">
          <div className="px-5 md:px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between bg-[#F9F9F9]">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#B40101]" />
              <span className="text-[13px] font-medium text-[#0A0A0A]">
                Il tuo caso · Anteprima Workspace
              </span>
            </div>
            <span className="text-[11px] uppercase tracking-widest text-[#4B5563]">
              Esempio VALUTA → DECIDI
            </span>
          </div>

          <div className="p-5 md:p-6 space-y-5">
            {/* Direzione scelta + analisi economica — riflette EconomicAnalysis di VALUTA */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-[#4B5563] mb-1">
                  Immobile
                </div>
                <div className="text-[#0A0A0A] text-[14px]">
                  Appartamento · 82 m² · Milano
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-[#4B5563] mb-1">
                  Direzione scelta
                </div>
                <div className="text-[#0A0A0A] text-[14px]">
                  Vendita dopo piccola preparazione
                </div>
              </div>
            </div>

            <div className="rounded-xl bg-[#F9F9F9] border border-[#E5E7EB] p-4">
              <div className="text-[11px] uppercase tracking-widest text-[#4B5563] mb-2 font-semibold">
                Netto stimato dalla valutazione*
              </div>
              <div className="flex items-baseline gap-3 flex-wrap">
                <span className="font-serif text-[#0A0A0A] text-2xl leading-none">
                  €&nbsp;322.000 – 358.000
                </span>
                <span className="text-[12px] text-[#4B5563]">
                  Fascia centrale · costi upfront ~€&nbsp;9.500
                </span>
              </div>
            </div>

            {/* Checklist DECIDI — riflette il formato reale (M8.5): item statici
                per famiglia scenario + stato per-item ce l'ho/non ce l'ho/non so */}
            <div>
              <div className="flex items-baseline justify-between mb-2">
                <div className="text-[11px] uppercase tracking-widest text-[#4B5563] font-semibold">
                  Checklist DECIDI · documenti per la vendita
                </div>
                <span className="text-[11px] text-[#4B5563]">
                  3/6 già in mano
                </span>
              </div>
              <ul className="space-y-1.5">
                <ChecklistPreviewRow
                  label="Attestato di Prestazione Energetica (APE)"
                  status="have"
                />
                <ChecklistPreviewRow
                  label="Planimetria catastale conforme allo stato di fatto"
                  status="missing"
                />
                <ChecklistPreviewRow
                  label="Conformità impianti (elettrico, idraulico)"
                  status="unknown"
                />
              </ul>
              <p className="text-[11px] text-[#4B5563] mt-2 italic">
                +3 voci in checklist. Rispondendo &ldquo;ce l&apos;ho / non
                ce l&apos;ho / non so&rdquo; il piano si aggiorna solo su
                ciò che ti resta da procurare.
              </p>
            </div>

            {/* Prossima azione — riflette next_action del piano operativo AGISCI */}
            <div className="rounded-xl bg-[#B40101]/[0.04] border border-[#B40101]/20 p-4">
              <div className="text-[11px] uppercase tracking-widest text-[#B40101] mb-2 font-semibold">
                Prossimo passo suggerito
              </div>
              <p className="text-[14px] text-[#0A0A0A] leading-relaxed">
                Recuperare la planimetria catastale aggiornata presso
                l&apos;Agenzia delle Entrate e verificare che
                corrisponda allo stato reale — prerequisito per il
                rogito.
              </p>
              <p className="text-[12px] text-[#4B5563] mt-2">
                Categoria: Geometra · costo indicativo 80–150 €
              </p>
            </div>

            <div className="flex items-center justify-between pt-1">
              <span className="text-[12px] text-[#4B5563]">
                Costruito automaticamente al termine di VALUTA → DECIDI
              </span>
              <span className="inline-flex items-center gap-1 text-[12px] text-[#B40101] font-semibold">
                <Check className="w-3.5 h-3.5" /> Decisione registrata
              </span>
            </div>
          </div>
        </div>

        <p className="mt-6 text-center text-[12px] text-[#4B5563]">
          * Vista esemplificativa. Numeri e voci mostrati non appartengono a
          un caso reale — sono un&apos;anteprima del formato prodotto
          dal percorso.
        </p>
      </div>
    </section>
  );
}


// §M8.8 · Riga singola della preview checklist — riflette esattamente
// il formato reale di DECIDI (§M8.5): documento + stato-pill colorato
// mappato sui tre valori possibili "ce l'ho / non ce l'ho / non so".
function ChecklistPreviewRow({ label, status }) {
  const cfg = {
    have:    { text: "Ce l'ho",       cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    missing: { text: "Non ce l'ho",   cls: "bg-red-100 text-red-800 border-red-200" },
    unknown: { text: "Non so",        cls: "bg-amber-100 text-amber-800 border-amber-200" },
  }[status] || { text: "—", cls: "bg-neutral-100 text-neutral-700 border-neutral-200" };
  return (
    <li className="flex items-center justify-between gap-3 text-[13px]">
      <span className="text-[#0A0A0A] flex-1 min-w-0 truncate">
        {label}
      </span>
      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap ${cfg.cls}`}>
        {cfg.text}
      </span>
    </li>
  );
}


// ---------- Owner Dashboard promo (§M8.7) --------------------------------

const OWNER_DASHBOARD_IMAGE = "https://customer-assets-4nw71qhi.emergentagent.net/job_immobiliare-tool/artifacts/9577j1ep_vendere%20casa%20ValutaGenio%20ti%20aiuta%20a%20gestire%20tutto.png";

// §M8.7 · Sezione promozione dashboard proprietari. Deve essere:
// - identica per utenti loggati e anonimi (vetrina informativa),
// - con sfondo parallax (background-attachment: fixed) solo su
//   desktop, statico su mobile per performance/accessibilità,
// - claim H2 sopra l'immagine, sottotitolo, 3 vantaggi con icone,
// - CTA che apre il pannello di iscrizione slide-in.
function OwnerDashboardSection({ onOpenSignup }) {
  return (
    <section
      id="owner-dashboard"
      data-testid="owner-landing-dashboard-section"
      className="relative border-b border-[#E5E7EB]"
    >
      {/* Parallax background layer · fixed on desktop, static on mobile.
          Using a dedicated absolute layer instead of `bg-fixed` on the
          section itself so the overlay + content stack correctly. */}
      <div
        aria-hidden="true"
        data-testid="owner-landing-dashboard-bg"
        className="absolute inset-0 bg-center bg-cover bg-no-repeat md:bg-fixed"
        style={{ backgroundImage: `url("${OWNER_DASHBOARD_IMAGE}")` }}
      />
      {/* Warm overlay for legibility over any part of the photo */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-br from-white/85 via-white/70 to-white/40 md:from-white/70 md:via-white/50 md:to-white/20"
      />

      <div className="relative max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-32">
        <div className="max-w-2xl">
          <span className="inline-block text-[11px] uppercase tracking-[0.22em] font-semibold text-[#B40101] mb-4">
            La scrivania proprietari
          </span>
          <h2
            data-testid="owner-landing-dashboard-claim"
            className="font-serif text-[#2B2B2B] text-3xl md:text-4xl lg:text-[46px] leading-[1.15] tracking-tight"
          >
            La scrivania per ogni immobile su cui stai decidendo.
          </h2>
          <p className="mt-5 text-[#0A0A0A]/85 text-[16px] md:text-[18px] leading-relaxed max-w-xl">
            Un profilo gratuito per raccogliere ciò che scopri sul tuo
            immobile, aggiornare i dati quando cambiano e rivedere le
            stime senza ripartire da zero. Un solo posto per il tuo
            caso — sempre a portata di mano.
          </p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6"
               data-testid="owner-landing-dashboard-benefits">
            <DashboardBenefit
              icon={<History strokeWidth={1.6} />}
              title="Storico casi salvato"
              desc="Ogni analisi che avvii resta nel tuo profilo, con la possibilità di riaprirla quando vuoi."
              testid="benefit-history"
            />
            <DashboardBenefit
              icon={<Edit3 strokeWidth={1.6} />}
              title="Modifica dati e rivedi la stima"
              desc="Cambia superficie, condizione, dettagli — la stima si aggiorna sul nuovo scenario."
              testid="benefit-edit"
            />
            <DashboardBenefit
              icon={<UserRound strokeWidth={1.6} />}
              title="Il tuo Property Advisor"
              desc="Quando serve un umano, ti connetti al tuo consulente dedicato dallo stesso posto."
              testid="benefit-advisor"
            />
          </div>

          <div className="mt-10">
            <button
              type="button"
              onClick={onOpenSignup}
              data-testid="owner-landing-dashboard-cta"
              className="
                inline-flex items-center gap-2
                bg-[#B40101] hover:bg-[#8F0101] text-white font-semibold
                text-base px-6 py-3.5 rounded-xl
                transition-all duration-200 hover:-translate-y-0.5
                shadow-[0_10px_30px_rgba(180,1,1,0.28)]
              "
            >
              Iscriviti gratuitamente <ArrowRight className="w-5 h-5" />
            </button>
            <p className="text-xs text-[#4B5563] mt-3">
              Nessuna carta richiesta. La tua analisi anonima viene
              collegata al profilo se hai già iniziato.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}


function DashboardBenefit({ icon, title, desc, testid }) {
  return (
    <div
      className="
        rounded-2xl border border-[#E5E7EB] bg-white/90 backdrop-blur-sm
        p-5 flex flex-col gap-2 shadow-[0_6px_18px_-8px_rgba(0,0,0,0.12)]
      "
      data-testid={testid}
    >
      <span
        className="
          w-10 h-10 inline-flex items-center justify-center
          rounded-full bg-[#B40101]/10 text-[#B40101]
        "
      >
        {icon}
      </span>
      <p className="font-serif text-[#2B2B2B] text-lg leading-tight mt-1">
        {title}
      </p>
      <p className="text-sm text-[#0A0A0A]/80 leading-relaxed">
        {desc}
      </p>
    </div>
  );
}


// ---------- 8. Final CTA -------------------------------------------------

function FinalCTA() {
  return (
    <section
      data-testid="owner-landing-final-cta"
      className="bg-white border-b border-[#E5E7EB]"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24 text-center">
        <h2 className="font-serif text-[#0A0A0A] text-3xl md:text-4xl lg:text-[46px] leading-[1.15] tracking-tight max-w-3xl mx-auto">
          La nostra missione è portare più cultura immobiliare a chi possiede casa.
        </h2>
        <p className="mt-6 text-[#0A0A0A]/85 text-[16px] md:text-[18px] leading-relaxed max-w-2xl mx-auto">
          Più capisci la tua situazione, più puoi scegliere con libertà e guidare tu il cambiamento. Leggi il magazine Valore Immobiliare per aggiornare il tuo bagaglio di conoscenze: parliamo di mercato e valutazioni, successioni e comproprietà, ristrutturazioni e messa a reddito. Intanto, puoi già iniziare a capire il potenziale del tuo immobile. Le tue informazioni restano private.
        </p>
        <div className="mt-9 flex justify-center">
          <Link
            to="/vg2"
            data-testid="owner-landing-cta-final"
            className="inline-flex items-center gap-2 bg-[#B40101] hover:bg-[#8F0101] text-white font-semibold text-base px-6 py-3.5 rounded-xl transition-all duration-200 hover:-translate-y-0.5 shadow-[0_10px_30px_rgba(180,1,1,0.28)]"
          >
            Scopri cosa puoi fare con il tuo immobile <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ========================================================================
// REVISIONE HOME · modello grafico allineato alla landing Professionisti.
// Immagini: solo asset Home già approvati/presenti. Nessuna generazione.
// ========================================================================

const HOME_IMG = "/images/home";

// 2 · LE POSSIBILITÀ — quattro casi in una riga (4 col desktop).
function PossibilitaSection() {
  const startWithIntent = useStartWithIntent();
  const cases = [
    { icon: Tag, label: "Voglio vendere", intentCode: "vendere", intentLabel: "Vendere l'immobile" },
    { icon: KeyRound, label: "Voglio affittare", intentCode: "generare_reddito", intentLabel: "Affittare l'immobile" },
    { icon: Hammer, label: "Voglio ristrutturare", intentCode: "capire_quale_scelta_conviene", intentLabel: "Ristrutturare o valorizzare l'immobile" },
    { icon: TrendingUp, label: "Voglio mettere a reddito", intentCode: "generare_reddito", intentLabel: "Mettere a reddito l'immobile" },
  ];
  return (
    <section
      data-testid="home-possibilita"
      className="bg-white border-b border-[#E5E7EB]"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24">
        <Eyebrow>Le possibilità</Eyebrow>
        <H2>Da dove vuoi partire?</H2>
        <Lead>
          Ogni immobile può diventare il punto di partenza per un progetto diverso. Che tu voglia vendere, affittare, ristrutturare o investire, scegli l&apos;obiettivo che desideri esplorare. ValutaGenio ti aiuterà a comprendere le possibilità e a individuare la strada più adatta.
        </Lead>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {cases.map(({ icon: Icon, label, intentCode, intentLabel }) => (
            <button
              key={label}
              type="button"
              onClick={() => startWithIntent(intentCode, intentLabel)}
              data-testid={`home-caso-${intentCode}-${label.split(' ').pop().toLowerCase()}`}
              className="group text-left rounded-2xl border border-[#E5E7EB] bg-white p-6 md:p-7 flex flex-col gap-4 hover:border-[#B40101] hover:-translate-y-0.5 transition-all duration-200"
            >
              <span className="w-12 h-12 rounded-full bg-[#B40101]/10 inline-flex items-center justify-center">
                <Icon className="w-6 h-6 text-[#B40101]" strokeWidth={1.8} />
              </span>
              <h3 className="font-semibold text-[#0A0A0A] text-lg leading-snug">{label}</h3>
              <span className="mt-auto text-sm font-semibold text-[#B40101] inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                Inizia <ArrowRight className="w-4 h-4" />
              </span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

// Reusable narrative block: desktop alterna foto/testo; mobile = foto sopra il testo.
function HomeBlock({ id, testid, imageSide, image, alt, eyebrow, title, paragraphs, bg = "bg-white", aspect = "aspect-[4/3]", children }) {
  const imgOrder = imageSide === "left" ? "lg:order-1" : "lg:order-2";
  const txtOrder = imageSide === "left" ? "lg:order-2" : "lg:order-1";
  return (
    <section id={id} data-testid={testid} className={`${bg} border-b border-[#E5E7EB]`}>
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className={`order-1 ${imgOrder} lg:col-span-6`}>
          <div className={`relative rounded-2xl overflow-hidden border border-[#E5E7EB] shadow-[0_24px_60px_-30px_rgba(0,0,0,0.25)] ${aspect}`}>
            <img src={image} alt={alt} data-testid={`${testid}-image`} className="w-full h-full object-cover" loading="lazy" decoding="async" />
          </div>
        </div>
        <div className={`order-2 ${txtOrder} lg:col-span-6`}>
          {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
          <H2>{title}</H2>
          <div className="mt-6 space-y-4">
            {paragraphs.map((p, i) => (
              <p key={i} className="text-[#0A0A0A]/80 text-[17px] md:text-[18px] leading-relaxed">{p}</p>
            ))}
          </div>
          {children}
        </div>
      </div>
    </section>
  );
}

// 3 · PRIMO BLOCCO FOTO/TESTO
function BloccoScelte() {
  return (
    <HomeBlock
      id="scelte"
      testid="home-scelte"
      imageSide="left"
      image={`${HOME_IMG}/VG_HOME_01_SCELTE.png`}
      alt="Persone che valutano le possibilità di un immobile"
      title="Ogni immobile apre nuove possibilità."
      paragraphs={[
        "Vendere, affittare, ristrutturare o investire sono scelte che richiedono informazioni, confronti e una visione chiara degli obiettivi.",
        "ValutaGenio ti aiuta a esplorare le alternative, riconoscere il potenziale dell'immobile e individuare una direzione.",
        "Inizia dalla valutazione gratuita dell'immobile di cui vuoi scoprire il potenziale.",
      ]}
    >
      <Link
        to="/vg2"
        data-testid="home-scelte-cta"
        className="mt-8 inline-flex items-center gap-2 bg-[#B40101] hover:bg-[#8F0101] text-white font-semibold text-base px-6 py-3.5 rounded-xl transition-all duration-200 hover:-translate-y-0.5 shadow-[0_10px_30px_rgba(180,1,1,0.28)]"
      >
        Inizia la valutazione gratuita <ArrowRight className="w-5 h-5" />
      </Link>
    </HomeBlock>
  );
}

// 4 · FOCUS CENTRALE — PARALLAX
function FocusParallax() {
  return (
    <section
      data-testid="home-focus-parallax"
      className="relative bg-scroll md:bg-fixed bg-center bg-cover border-b border-[#E5E7EB]"
      style={{ backgroundImage: `url(${HOME_IMG}/VG_HOME_02_FOCUS.png)` }}
    >
      <div className="absolute inset-0 bg-[#0A0A0A]/55" aria-hidden="true" />
      <div className="relative max-w-[1200px] mx-auto px-5 md:px-8 py-24 md:py-36 text-center">
        <h2 className="font-serif text-white text-3xl md:text-5xl leading-[1.1] tracking-tight max-w-3xl mx-auto">
          Ogni immobile ha possibilità da scoprire.
        </h2>
      </div>
    </section>
  );
}

// 5 · PERCORSO VALUTA · DECIDI · AGISCI (foto/testo alternato)
function PercorsoSection() {
  return (
    <>
      <HomeBlock
        id="valuta"
        testid="home-valuta"
        imageSide="left"
        image="/images/home/VG_PA_04_OPPORTUNITA.png"
        alt="Scopri il potenziale e le opportunità del tuo immobile"
        eyebrow="VALUTA"
        title="Comprendi il potenziale"
        paragraphs={[
          "Ogni operazione immobiliare comincia dalla conoscenza. Descrivi il tuo immobile, le sue caratteristiche, la posizione e ciò che desideri ottenere. ValutaGenio organizza le informazioni, analizza il contesto e ti restituisce una prima valutazione orientativa, aiutandoti a riconoscere punti di forza, criticità e possibilità ancora inesplorate. È il primo passo per prendere decisioni più consapevoli.",
        ]}
      />
      <HomeBlock
        id="decidi"
        testid="home-decidi"
        imageSide="right"
        bg="bg-[#F9F9F9]"
        image="/images/percorso/vg-percorso-tre-domande.webp"
        alt="Confronto tra scenari e alternative per l'immobile"
        eyebrow="DECIDI"
        title="Confronta le alternative"
        paragraphs={[
          "Non esiste sempre una sola strada per valorizzare un immobile. Vendere subito, affittare, ristrutturare o sviluppare un investimento possono produrre risultati molto diversi. ValutaGenio ti aiuta a esplorare gli scenari, confrontare opportunità, costi e benefici quando i dati lo consentono, e comprendere quali alternative rispondono meglio ai tuoi obiettivi. Così puoi individuare una direzione prima di impegnare tempo e risorse.",
        ]}
      />
      <HomeBlock
        id="agisci"
        testid="home-agisci"
        imageSide="left"
        image="/images/vg2/vg-agisci-property-advisor.webp"
        alt="Confronto con un Property Advisor per dare seguito alle scelte"
        eyebrow="AGISCI"
        title="Dai seguito alle tue scelte"
        paragraphs={[
          "Una buona decisione diventa davvero utile quando si trasforma in un progetto concreto. Dopo aver analizzato l'immobile e confrontato le alternative, puoi individuare i passi successivi, le verifiche necessarie e le competenze da coinvolgere. E quando desideri passare dall'analisi all'azione, puoi richiedere un primo confronto gratuito con un Property Advisor: il professionista che ti aiuta a dare forma al tuo progetto immobiliare.",
        ]}
      />
    </>
  );
}

// 6 · IL SUPPORTO PROFESSIONALE (foto/testo alternato)
function SupportoProfessionaleSection() {
  return (
    <HomeBlock
      id="supporto"
      testid="home-supporto"
      imageSide="right"
      bg="bg-[#F9F9F9]"
      image="/images/percorso/vg-percorso-property-advisor.webp"
      alt="Un Property Advisor affianca il proprietario nelle scelte immobiliari"
      eyebrow="Il supporto professionale"
      title="La tecnologia individua le possibilità. L'esperienza aiuta a realizzarle."
      paragraphs={[
        "Avere al proprio fianco un professionista esperto può fare la differenza tra un'idea interessante e un progetto realizzabile. Un Property Advisor mette a disposizione esperienza, conoscenza del mercato e capacità di interpretare le opportunità, aiutandoti a riconoscere le priorità e a scegliere come procedere.",
        "Con ValutaGenio puoi richiedere un primo incontro gratuito di circa 30 minuti. Completa il percorso VALUTA → DECIDI → AGISCI e prenota il tuo confronto: tu e il professionista avrete già a disposizione le informazioni raccolte e una prima visione delle alternative, per impostare al meglio il tuo progetto immobiliare.",
      ]}
    >
      <Link
        to="/vg2"
        data-testid="home-supporto-cta"
        className="mt-8 inline-flex items-center gap-2 bg-[#B40101] hover:bg-[#8F0101] text-white font-semibold text-base px-6 py-3.5 rounded-xl transition-all duration-200 hover:-translate-y-0.5 shadow-[0_10px_30px_rgba(180,1,1,0.28)]"
      >
        Inizia il percorso gratuito <ArrowRight className="w-5 h-5" />
      </Link>
    </HomeBlock>
  );
}

// 7 · SCRIVANIA PERSONALE — sezione finale autonoma
function ScrivaniaSection({ onOpenSignup }) {
  return (
    <section
      id="scrivania"
      data-testid="home-scrivania"
      className="bg-white border-b border-[#E5E7EB]"
    >
      <div className="max-w-[920px] mx-auto px-5 md:px-8 py-16 md:py-24">
        <Eyebrow>La tua Scrivania</Eyebrow>
        <H2>Le tue valutazioni, sempre a disposizione.</H2>
        <p className="mt-6 text-[#0A0A0A]/80 text-[17px] md:text-[18px] leading-relaxed max-w-2xl">
          Da oggi puoi avere a tua disposizione una Scrivania Personale gratuita, uno spazio dedicato ai tuoi progetti immobiliari. Qui puoi salvare le analisi, riaprirle, aggiornarle e ricalcolarle quando cambiano le informazioni, le condizioni del mercato o i tuoi obiettivi.
        </p>
        <p className="mt-4 text-[#0A0A0A]/80 text-[17px] md:text-[18px] leading-relaxed max-w-2xl">
          La tua Scrivania può accompagnarti anche nelle fasi successive, con funzionalità avanzate per approfondire le analisi economiche, confrontare scenari, sviluppare piani d&apos;azione e seguire l&apos;evoluzione del tuo progetto immobiliare, dall&apos;idea iniziale fino alla sua realizzazione.
        </p>
        <div className="mt-9">
          <button
            type="button"
            onClick={onOpenSignup}
            data-testid="home-scrivania-cta"
            className="inline-flex items-center gap-2 bg-[#B40101] hover:bg-[#8F0101] text-white font-semibold text-base px-6 py-3.5 rounded-xl transition-all duration-200 hover:-translate-y-0.5 shadow-[0_10px_30px_rgba(180,1,1,0.28)]"
          >
            Apri la tua dashboard gratuitamente <ArrowRight className="w-5 h-5" />
          </button>
          <p className="text-xs text-[#4B5563] mt-3">
            Nessuna carta richiesta. La tua analisi anonima viene collegata al profilo se hai già iniziato.
          </p>
        </div>
      </div>
    </section>
  );
}

// ---------- page ---------------------------------------------------------

function MagazineTeaserSection() {
  const items = getLatestArticles(2);
  if (!items.length) return null;
  return (
    <section
      data-testid="owner-landing-magazine-teaser"
      className="bg-white border-b border-[#E5E7EB]"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-6">
          <div>
            <p className="uppercase tracking-[0.24em] text-[11px] md:text-xs font-semibold text-[#B40101]">
              Dal magazine {MAGAZINE_META.name}
            </p>
            <h2 className="mt-3 font-display font-extrabold text-[#1F2937] tracking-tight text-3xl md:text-4xl lg:text-5xl leading-[1.1]">
              Capire la casa. Decidere meglio.
            </h2>
          </div>
          <Link
            to={MAGAZINE_META.base_path}
            data-testid="owner-landing-magazine-link-all"
            className="inline-flex items-center gap-2 text-[#B40101] hover:text-[#8F0101] font-semibold text-sm md:text-base transition-colors"
          >
            Vedi tutti gli articoli
            <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
          </Link>
        </div>

        <div className="mt-10 md:mt-12 grid md:grid-cols-2 gap-6 md:gap-8">
          {items.map((a, i) => (
            <Link
              key={a.slug}
              to={`${MAGAZINE_META.base_path}/${a.slug}`}
              data-testid={`owner-landing-magazine-card-${i + 1}`}
              className="group bg-white rounded-2xl overflow-hidden shadow-sm ring-1 ring-black/5 hover:shadow-md transition-shadow"
            >
              <div className="aspect-[16/9] overflow-hidden">
                <img
                  src={a.cover_image.src}
                  alt={a.cover_image.alt}
                  width={a.cover_image.width}
                  height={a.cover_image.height}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                />
              </div>
              <div className="p-6 md:p-7">
                <span className="inline-flex items-center uppercase tracking-[0.18em] text-[10px] md:text-[11px] font-semibold text-[#B40101]">
                  {a.category_label}
                </span>
                <h3 className="mt-3 font-display font-extrabold text-[#1F2937] tracking-tight leading-[1.1] text-xl md:text-2xl group-hover:text-[#B40101] transition-colors">
                  {a.title}
                </h3>
                <p className="mt-3 text-[#1F2937] text-base leading-relaxed font-light line-clamp-3">
                  {a.summary}
                </p>
                <div className="mt-5 inline-flex items-center gap-2 text-[#B40101] font-semibold text-sm">
                  Leggi l’articolo
                  <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-10 md:mt-12 flex justify-center">
          <Link
            to={MAGAZINE_META.base_path}
            data-testid="owner-landing-magazine-cta-all"
            className="inline-flex items-center gap-3 bg-[#B40101] hover:bg-[#8F0101] text-white font-semibold text-base md:text-lg px-6 md:px-8 py-3.5 md:py-4 rounded-xl transition-colors shadow-sm"
          >
            <span>Vedi tutti gli articoli</span>
            <ArrowRight className="w-5 h-5" strokeWidth={2.2} />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function OwnerLanding() {
  useLandingSEO();
  const { openPanel, Panel } = useOwnerSignupPanel();

  // §M8.7 · Auto-scroll to #owner-dashboard when the URL carries that
  // hash — reminder links from CAPISCI/VALUTA/DECIDI open the home in
  // a new tab and expect the section to be immediately visible.
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash !== "#owner-dashboard") return;
    const el = document.getElementById("owner-dashboard");
    if (!el) return;
    // Defer to next tick to let the section mount + parallax bg render.
    const t = window.setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <main
      data-testid="owner-landing-page"
      className="bg-white text-[#0A0A0A] antialiased overflow-x-hidden"
    >
      <LandingNavbar />
      <Hero />
      <PossibilitaWindow />
      <BloccoScelte />
      <FocusParallax />
      <PercorsoSection />
      <SupportoProfessionaleSection />
      <ScrivaniaSection onOpenSignup={openPanel} />
      <Footer />
      <Panel />
    </main>
  );
}
